/**
 * Storage Overview Widget
 * 
 * Displays platform-wide storage statistics including:
 * - Total storage used vs quota with progress bar
 * - Key metrics: tenants, documents, active users
 */

import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui';
import { Progress } from '@/components/ui/progress';
import { usePlatformStats } from '../hooks';
import { HardDrive, Users, FileText, Building2 } from 'lucide-react';

function WidgetSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-2 w-full" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function StorageOverviewWidget() {
  const { data: stats, isLoading, error } = usePlatformStats();

  if (isLoading) {
    return <WidgetSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Platform Storage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Failed to load storage stats
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const storagePercent = stats.storage_utilization_percent ?? 0;
  const isHighUsage = storagePercent > 80;
  const isCriticalUsage = storagePercent > 90;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          Platform Storage
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Storage Progress */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Used</span>
              <span className={isCriticalUsage ? 'text-destructive font-medium' : ''}>
                {stats.total_storage_used_gb.toFixed(1)} GB / {stats.total_storage_quota_gb} GB
              </span>
            </div>
            <Progress 
              value={storagePercent} 
              className={isCriticalUsage ? '[&>div]:bg-destructive' : isHighUsage ? '[&>div]:bg-amber-500' : ''}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {storagePercent.toFixed(1)}% utilized
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 text-center pt-2 border-t">
            <div>
              <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                {stats.total_tenants}
              </div>
              <div className="text-xs text-muted-foreground">Tenants</div>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                <FileText className="h-4 w-4 text-muted-foreground" />
                {stats.total_documents}
              </div>
              <div className="text-xs text-muted-foreground">Documents</div>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                <Users className="h-4 w-4 text-muted-foreground" />
                {stats.total_active_users_30d}
              </div>
              <div className="text-xs text-muted-foreground">Active Users</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
